/**
 * O que a empresa já configurou — a medição por trás de `/primeiros-passos`.
 *
 * Direção A da proposta de 2026-09-11
 * (`docs/designsystem/propostas/primeiros-passos.html`), escolhida em 26/09.
 *
 * A tela antiga era um `array` constante: mostrava os mesmos três cartões no
 * primeiro dia e no ano seguinte, e na base real os três passos já estavam
 * feitos. Aqui cada passo é uma LEITURA — o que a tela diz vem do que existe.
 *
 * Três regras que vale manter ao mexer:
 *
 * 1. **Só se mede o que a pessoa pode ver.** O chamador passa os ids que
 *    sobraram do filtro de permissão; passo sem permissão não vira requisição.
 *    Fora a economia, isso evita 403 no console de quem não é administrador.
 * 2. **Falha de uma leitura não derruba a tela.** Cada passo tem o seu
 *    `try/catch` e vira `indisponivel`; a tela mostra "não foi possível
 *    conferir" naquela linha e as outras sete continuam valendo.
 * 3. **Nenhuma leitura é nova.** Os oito endpoints já existem e já são usados
 *    por outras telas — nenhuma mudança de API foi necessária.
 */

import { ProductService } from './ProductService';
import { RegrasIAService } from './RegrasIAService';
import {
    ConexaoPlataformaService,
    StatusConexaoEnum,
    getPlatformLabel,
} from './ConexaoPlataformaService';
import EquipeService from './EquipeService';
import MetaGoalService from './MetaGoalService';
import { MotivoPerdaService } from './MotivoPerdaService';
import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

export type PassoId =
    | 'produtos'
    | 'agente'
    | 'canais'
    | 'horario'
    | 'usuarios'
    | 'equipes'
    | 'metas'
    | 'motivos';

export interface SinalPasso {
    feito: boolean;
    /** Uma linha: a evidência quando está feito, o que falta quando não está. */
    detalhe: string;
    /** A leitura não respondeu — nem feito nem pendente, indeterminado. */
    indisponivel?: boolean;
}

export type MapaSinais = Partial<Record<PassoId, SinalPasso>>;

const plural = (n: number, um: string, muitos: string) =>
    `${n} ${n === 1 ? um : muitos}`;

/** "setembro", "outubro" — para a linha das metas falar do mês corrente. */
export const mesPorExtenso = (d = new Date()) =>
    d.toLocaleDateString('pt-BR', { month: 'long' });

/** "2026-09" — o formato que `meta/BuscarTodas` espera. */
const mesIso = (d = new Date()) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const DIAS: { chave: keyof DiasUteisResposta; curto: string }[] = [
    { chave: 'segunda', curto: 'seg' },
    { chave: 'terca', curto: 'ter' },
    { chave: 'quarta', curto: 'qua' },
    { chave: 'quinta', curto: 'qui' },
    { chave: 'sexta', curto: 'sex' },
    { chave: 'sabado', curto: 'sáb' },
    { chave: 'domingo', curto: 'dom' },
];

interface DiasUteisResposta {
    segunda: boolean;
    terca: boolean;
    quarta: boolean;
    quinta: boolean;
    sexta: boolean;
    sabado: boolean;
    domingo: boolean;
}

const resumoDias = (d: DiasUteisResposta): string => {
    const ligados = DIAS.filter(dia => d[dia.chave]);
    if (ligados.length === 0) return 'Nenhum dia de atendimento';
    if (ligados.length === 7) return 'Todos os dias';
    const seguidos = DIAS.filter(dia => d[dia.chave]).map(dia => dia.curto);
    const indices = DIAS.map((dia, i) => (d[dia.chave] ? i : -1)).filter(i => i >= 0);
    const contiguo = indices.every((v, i) => i === 0 || v === indices[i - 1] + 1);
    return contiguo && ligados.length > 2
        ? `${seguidos[0]} a ${seguidos[seguidos.length - 1]}`
        : seguidos.join(', ');
};

/* ── as oito leituras ────────────────────────────────────────────────────── */

const lerProdutos = async (): Promise<SinalPasso> => {
    const r = await ProductService.getAll();
    const itens = r.dados ?? [];
    if (itens.length === 0) {
        return { feito: false, detalhe: 'Nenhum produto — o agente não tem o que oferecer' };
    }
    return {
        feito: true,
        detalhe: itens.length === 1
            ? `1 produto · ${itens[0].nome}`
            : plural(itens.length, 'produto cadastrado', 'produtos cadastrados'),
    };
};

const lerAgente = async (): Promise<SinalPasso> => {
    const r = await RegrasIAService.getAgentes();
    const agentes = r.dados ?? [];
    if (agentes.length === 0) {
        return {
            feito: false,
            detalhe: 'Nenhum agente — tom de voz, objetivo e o que ele sabe da empresa',
        };
    }
    const padrao = agentes.find(a => a.isDefault) ?? agentes[0];
    const respondendo = padrao.botAtivo ? 'respondendo' : 'com o bot desligado';
    return {
        feito: true,
        detalhe: agentes.length === 1
            ? `${padrao.nomeAgente} — padrão, ${respondendo}`
            : `${padrao.nomeAgente} — padrão, ${respondendo} · ${agentes.length} agentes`,
    };
};

const lerCanais = async (): Promise<SinalPasso> => {
    const r = await ConexaoPlataformaService.buscarConexoesAtivas();
    const ativas = (r.dados ?? []).filter(c => c.status === StatusConexaoEnum.Ativa);
    if (ativas.length === 0) {
        return {
            feito: false,
            detalhe: 'Nenhum canal ativo — WhatsApp, Instagram ou Lead Ads',
        };
    }
    const nomes = [...new Set(ativas.map(c => getPlatformLabel(c.plataforma)))];
    return {
        feito: true,
        detalhe: `${plural(ativas.length, 'canal ativo', 'canais ativos')} · ${nomes.join(', ')}`,
    };
};

/**
 * O único passo cuja resposta não se lê pelos dados.
 *
 * `gerenciarempresa/DiasUteis` devolve **segunda a sexta** tanto para quem
 * configurou assim quanto para quem nunca configurou — o back monta um padrão
 * quando não acha registro (`GerenciarEmpresaService.BuscarDiasUteisPorEmpresa`,
 * no repo da API). O que separa os dois casos é só a MENSAGEM:
 * "Configuração padrão." × "Configuração encontrada.".
 *
 * Ler string de mensagem é frágil — se alguém reescrever o texto no back, este
 * passo passa a dizer "pendente" para todo mundo, sem erro nenhum aparecer. Por
 * isso o fallback é o lado seguro (na dúvida, FEITO: a empresa atende seg–sex de
 * fato, então cobrar configuração seria mentira), e a correção certa está
 * proposta no documento: um booleano `configurado` na resposta da API.
 */
const lerHorario = async (): Promise<SinalPasso> => {
    const res = await apiFetch(`${API_URL}/api/gerenciarempresa/DiasUteis`);
    const data = await res.json();
    if (!data.sucesso) throw new Error(data.mensagem);

    const nuncaConfigurou = typeof data.mensagem === 'string'
        && data.mensagem.toLowerCase().includes('padrão');

    if (nuncaConfigurou) {
        return {
            feito: false,
            detalhe: 'Ainda no padrão seg a sex — o ritmo das metas usa dias úteis',
        };
    }
    return { feito: true, detalhe: resumoDias(data.dados as DiasUteisResposta) };
};

const lerUsuarios = async (): Promise<SinalPasso> => {
    const res = await apiFetch(`${API_URL}/api/Usuario/BuscarTodos`);
    const data = await res.json();
    if (!data.sucesso) throw new Error(data.mensagem);
    const total = (data.dados ?? []).length;
    /* Uma pessoa só é a própria conta de quem abriu: ninguém foi convidado
       ainda, então o passo continua pendente. */
    if (total <= 1) return { feito: false, detalhe: 'Só você — ninguém convidado ainda' };
    return { feito: true, detalhe: plural(total, 'usuário', 'usuários') };
};

const lerEquipes = async (): Promise<SinalPasso> => {
    const equipes = await EquipeService.buscarTodas();
    if (equipes.length === 0) return { feito: false, detalhe: 'Nenhuma equipe criada' };
    return {
        feito: true,
        detalhe: equipes.length === 1
            ? `1 equipe · ${equipes[0].nome}`
            : plural(equipes.length, 'equipe', 'equipes'),
    };
};

const lerMetas = async (): Promise<SinalPasso> => {
    const mes = mesPorExtenso();
    const metas = await MetaGoalService.buscarTodas(mesIso());
    if (metas.length === 0) return { feito: false, detalhe: `Nenhuma meta em ${mes}` };
    return { feito: true, detalhe: `${plural(metas.length, 'meta', 'metas')} em ${mes}` };
};

const lerMotivos = async (): Promise<SinalPasso> => {
    const r = await MotivoPerdaService.getAll();
    const itens = r.dados ?? [];
    if (itens.length === 0) {
        return {
            feito: false,
            detalhe: 'Nenhum motivo — sem eles o funil não explica o que perdeu',
        };
    }
    return { feito: true, detalhe: plural(itens.length, 'motivo', 'motivos cadastrados') };
};

const LEITURAS: Record<PassoId, () => Promise<SinalPasso>> = {
    produtos: lerProdutos,
    agente: lerAgente,
    canais: lerCanais,
    horario: lerHorario,
    usuarios: lerUsuarios,
    equipes: lerEquipes,
    metas: lerMetas,
    motivos: lerMotivos,
};

export const OnboardingStatusService = {
    /**
     * Mede os passos pedidos, todos em paralelo. Só entram os ids que o
     * chamador já filtrou por permissão.
     */
    medir: async (ids: PassoId[]): Promise<MapaSinais> => {
        const pares = await Promise.all(
            ids.map(async (id): Promise<[PassoId, SinalPasso]> => {
                try {
                    return [id, await LEITURAS[id]()];
                } catch {
                    return [id, {
                        feito: false,
                        indisponivel: true,
                        detalhe: 'Não foi possível conferir agora',
                    }];
                }
            }),
        );
        return Object.fromEntries(pares) as MapaSinais;
    },
};

export default OnboardingStatusService;
